import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { FormField } from 'common/components/FormField';
import { MessageBox } from 'common/components/MessageBox';
import { Button } from 'common/components/buttons/Button';
import { TextField } from 'common/components/TextField';

export type TTwoFactorFormValues = {
    code: string;
};

interface IProps {
    className?: string;
    loading: boolean;
    onRetryClick(): void;
    onSubmit(values: TTwoFactorFormValues): void;
}

const RETRY_INTERVAL_SEC = 60;

const BaseTwoFactorAuthForm = ({
    className,
    loading,
    onRetryClick,
    onSubmit,
}: IProps) => {
    const { t } = useTranslation();
    const [retryTimer, setRetryTimer] = useState(RETRY_INTERVAL_SEC);

    useEffect(() => {
        const intervalId = setInterval(() => {
            if (retryTimer > 0) {
                setRetryTimer(retryTimer - 1);
            }
        }, 1000);

        return () => clearInterval(intervalId);
    }, [retryTimer]);

    const handleRetryClick = () => {
        setRetryTimer(RETRY_INTERVAL_SEC);

        onRetryClick();
    };

    const formik = useFormik({
        initialValues: {
            code: '',
        },
        validationSchema: Yup.object().shape({
            code: Yup.string()
                .trim()
                .matches(/^[0-9]{6}$/, t('validators.twoFactor.invalid'))
                .required(t('validators.twoFactor.required')),
        }),
        onSubmit: onSubmit,
    });

    const {
        values,
        errors: formikErrors,
        touched,
        handleChange,
        handleSubmit,
    } = formik;

    const getFieldError = (fieldName: keyof TTwoFactorFormValues) => {
        if (touched[fieldName] && formikErrors[fieldName]) {
            return formikErrors[fieldName];
        }

        return '';
    };

    return (
        <form className={className} onSubmit={handleSubmit}>
            <Typography color="primary" variant="h2">
                {t('account.twoFactor.title')}
            </Typography>

            <Box className="description">
                <Typography color="textSecondary" variant="body2">
                    {t('account.twoFactor.instructions')}
                </Typography>
            </Box>

            <FormField>
                <TextField
                    error={getFieldError('code')}
                    id="code"
                    label={t('formfields.twoFactor')}
                    maxLength={6}
                    type="text"
                    value={values.code}
                    variant="filled"
                    onChange={handleChange}
                />
            </FormField>

            <Box mb={7} mt={4}>
                <Button loading={loading} onClick={() => handleSubmit()}>
                    {t('account.twoFactor.submit')}
                </Button>
            </Box>

            <MessageBox title={t('account.twoFactor.helpTitle')}>
                <Box component="ul">
                    <Box className="listItem" component="li">
                        {retryTimer ? (
                            `${t(
                                'account.twoFactor.retry_1'
                            )} ${retryTimer} ${t('account.twoFactor.retry_2')}`
                        ) : (
                            <span
                                className="retryLink"
                                onClick={handleRetryClick}
                            >
                                {t('account.twoFactor.retryLink')}
                            </span>
                        )}
                    </Box>
                    <Box className="listItem" component="li">
                        {t('account.twoFactor.contact')}
                    </Box>
                </Box>
            </MessageBox>
        </form>
    );
};

export const TwoFactorAuthForm = styled(BaseTwoFactorAuthForm)`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;

    .description {
        margin-bottom: ${({ theme }) => `${theme.spacing(10)}px`};
    }

    ${MessageBox} {
        margin-top: auto;
    }

    .listItem {
        position: relative;
        left: -7px;
        list-style-type: initial;
        margin-left: ${({ theme }) => `${theme.spacing(5)}px`};
        padding: 3px 0;
    }

    .retryLink {
        cursor: pointer;
    }
`;
